import { WsClient, type ApiRequestConfig, type WsMessage } from "./wssClient";
import type { Response } from "@/api/types";
import eventSub, { EventName } from "@/lib/EventSub.ts";

// 全局的 WebSocket 客户端实例
let wsClient: WsClient | null = null;

/**
 * 初始化 WebSocket 客户端
 * @param url - WebSocket 服务器地址
 */
export function initWsClient(url: string): WsClient {
  if (!wsClient) {
    console.log("initWsClient", url);
    wsClient = new WsClient({
      url,
      requestTimeout: 30000,
      connectTimeout: 30000,
    });
  }
  return wsClient;
}

/**
 * 构建请求消息
 * @param config - 请求配置
 * @returns 构建好的消息
 */
function buildRequestMessage(
  config: ApiRequestConfig
): Omit<WsMessage, "requestId"> {
  if (!config.url) {
    throw new Error("URL is required");
  }

  const { url, method = "POST", data = {}, headers = {} } = config;

  // 构建请求头
  const requestHeaders: Record<string, string> = {
    "content-type": "application/json",
  };

  // 转换 headers（处理 axios 的 AxiosHeaders 类型）
  if (headers) {
    if (typeof headers === "object" && !Array.isArray(headers)) {
      Object.entries(headers).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          requestHeaders[key] = String(value);
        }
      });
    }
  }

  // 注意：token 会在 send 方法中添加到请求体中，不需要在这里添加到 headers

  // 处理 method
  const methodStr =
    typeof method === "string"
      ? method.toUpperCase()
      : String(method).toUpperCase();
  requestHeaders.method = methodStr;

  return {
    api: url,
    method: methodStr,
    headers: requestHeaders,
    data,
  };
}

/**
 * 发送 WebSocket 请求
 * @param config - 请求配置
 * @returns Promise<[response, error]>
 */
export async function wssSend(
  config: ApiRequestConfig
): Promise<[unknown, Error | null]> {
  if (!wsClient) {
    throw new Error(
      "WebSocket client not initialized. Call initWsClient() first."
    );
  }

  // 构建请求消息
  const message = buildRequestMessage(config);

  // 发送消息
  const [response, error] = await wsClient.send(message);

  return [response, error];
}

/**
 * API 请求函数
 * @param config - 请求配置（使用 axios 的类型）
 * @returns Promise<Response<T>>
 */
export async function apiRequest<T>(
  config: ApiRequestConfig
): Promise<Response<T>> {
  if (!config.url) {
    throw new Error("URL is required");
  }

  const [response, error] = await wssSend(config);

  if (error) {
    throw error;
  }

  // 检查响应格式
  if (response && typeof response === "object" && "code" in response) {
    const res = response as Response<T>;
    if (res.code === 401) {
      eventSub.publish(EventName.NoAuth);
    }
    // 检查错误码（200 或 0 表示成功）
    if (res.code !== 200 && res.code !== 0) {
      throw new Error(res.message || `Request failed with code ${res.code}`);
    }
    return res;
  }

  // 如果没有标准格式，返回包装后的响应
  return {
    code: 200,
    message: "success",
    data: response as T,
  };
}
